import useApiRequest from 'hooks/useApiRequest';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { BookingHost } from 'types/booking';
import type { BookingHostFilters, Pagination } from 'types/filters';
import bookingHostsParamsBuilder from '../../services/bookingHostsParamsBuilder';
import { AuthContext } from '../../state/auth';

const sortHosts = (
  a: { firstName: string; lastName: string },
  b: { firstName: string; lastName: string }
) =>
  `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);

interface UseBookingHostsOptions {
  filters?: BookingHostFilters;
  sortBy?: string;
  pagination?: Pagination;
}

const useBookingHosts = ({
  filters,
  sortBy,
  pagination,
}: UseBookingHostsOptions = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [bookingHosts, setBookingHosts] = useState<BookingHost[]>([]);
  const [metaData, setMetaData] = useState('{}');

  const apiRequest = useApiRequest();

  const { loggedIn, currentClient } = useContext(AuthContext);

  const requestValid = loggedIn && currentClient;

  // Serialise the parameters so that the hooks only fire when the
  // actual content changes
  const bookingHostParamsJson = useMemo(
    () =>
      JSON.stringify(bookingHostsParamsBuilder(filters, sortBy, pagination)),
    [filters, sortBy, pagination]
  );

  const refresh = useCallback(async () => {
    if (!requestValid) {
      setBookingHosts([]);
      return null;
    }

    try {
      setLoading(true);

      if (bookingHostParamsJson !== '{}') {
        const {
          data: {
            bookingHosts: apiItems = [],
            meta: { pagination },
          },
        } = await apiRequest.get(
          `/v1/clients/${currentClient}/booking_hosts/search`,
          {
            params: JSON.parse(bookingHostParamsJson),
          }
        );

        setBookingHosts(apiItems);
        setMetaData(
          JSON.stringify({
            pagination,
            allBookingHostsCount: pagination.totalCount,
          })
        );
      } else {
        const {
          data: { bookingHosts: hosts = [] },
        } = await apiRequest.get(`/v1/clients/${currentClient}/booking_hosts`);

        setBookingHosts(hosts.sort(sortHosts));
        setMetaData('{}');
      }

      setLoading(false);
      setError(false);
      return null;
    } catch (_e) {
      setError(true);
      return null;
    }
  }, [apiRequest, currentClient, requestValid, bookingHostParamsJson]);

  const add = useCallback(
    async (bookingHost: object) => {
      if (!requestValid) throw new Error('Not logged in');

      try {
        const { data: host } = await apiRequest.post(
          `/v1/clients/${currentClient}/booking_hosts`,
          {
            bookingHost,
          }
        );

        setBookingHosts(prev => [...prev, host].sort(sortHosts));

        return host;
      } catch (e) {
        setError(true);
        throw e;
      }
    },
    [apiRequest, currentClient, requestValid]
  );

  const update = useCallback(
    async (bookingHost: Pick<BookingHost, 'id'> & object) => {
      if (!requestValid) throw new Error('Not logged in');

      try {
        const { data: host } = await apiRequest.patch(
          `/v1/clients/${currentClient}/booking_hosts/${bookingHost.id}`,
          {
            bookingHost,
          }
        );

        await new Promise(resolve => setTimeout(resolve, 1000));

        await refresh();

        return host;
      } catch (e) {
        setError(true);
        throw e;
      }
    },
    [apiRequest, currentClient, requestValid, refresh]
  );

  const meta = useMemo(() => JSON.parse(metaData), [metaData]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    data: bookingHosts,
    loading,
    error,
    refresh,
    add,
    update,
    meta,
  };
};

export default useBookingHosts;
